import examples from 'libphonenumber-js/examples.mobile.json';
import metadata from 'libphonenumber-js/metadata.min.json';
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js/min';

/**
 * Classifies what the user typed into the login field and normalises it into
 * the `login_hint` Auth0 expects for the matching passwordless connection:
 * E.164 for `sms`, a lowercase address for `email`.
 *
 * Auth0 resolves a hint without a country code against its default country
 * (US), so the number is always parsed against the country picked in the
 * dropdown. Anything that unambiguously means the same number is cleaned up
 * ("+44 7911…", "44 7911…", "0044 7911…", "+44 (0)7911…", "7911…" with GB
 * picked); a `+` prefix for a *different* country than the dropdown is an
 * error rather than silently overriding the user's choice.
 */
export type LoginIdentifier =
  | { kind: 'sms'; loginHint: string }
  | { kind: 'email'; loginHint: string }
  | { kind: 'invalid'; reason: LoginErrorReason; details?: LoginErrorDetails };

export type LoginErrorReason =
  | 'empty'
  | 'malformed'
  | 'tooShort'
  | 'tooLong'
  | 'countryMismatch'
  | 'invalidForCountry';

/** Interpolation values for the `login.errors.*` messages. */
// A type alias (not an interface) so it satisfies i18next's dictionary type.
export type LoginErrorDetails = {
  /** Name of the country picked in the dropdown, e.g. "United Kingdom". */
  selectedCountry: string;
  /** Calling code of the picked country, e.g. "+44". */
  selectedCode: string;
  /** Country the typed prefix belongs to, e.g. "France" (mismatch only). */
  typedCountry?: string;
  /** Calling code the user typed, e.g. "+33" (mismatch only). */
  typedCode?: string;
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DEFAULT_LOGIN_COUNTRY: CountryCode = 'GB';

export interface LoginCountry {
  code: CountryCode;
  name: string;
  callingCode: string;
}

const countryNames =
  typeof Intl !== 'undefined' && 'DisplayNames' in Intl
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : undefined;

export const countryName = (code: CountryCode): string =>
  countryNames?.of(code) ?? code;

let cachedCountries: LoginCountry[] | undefined;

/** Every country libphonenumber knows, sorted by English name. */
export function getLoginCountries(): LoginCountry[] {
  if (!cachedCountries) {
    cachedCountries = getCountries()
      .map((code) => ({
        code,
        name: countryName(code),
        callingCode: `+${getCountryCallingCode(code)}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, 'en'));
  }
  return cachedCountries;
}

/** Example mobile number in national format, e.g. "07400 123456" for GB. */
export const examplePhoneNumber = (country: CountryCode): string =>
  getExampleNumber(country, examples)?.formatNational() ?? '';

/** Main country for a calling code shared by several (+44 → GB, +1 → US). */
const countryForCallingCode = (callingCode: string): CountryCode | undefined =>
  metadata.country_calling_codes[callingCode]?.[0];

// Digits plus the punctuation people paste along with numbers.
const PHONE_CHARS = /^[+\d\s().-]+$/;

/**
 * Normalises the international-prefix variants to a single leading `+`:
 * "0044 …" and "++44 …" → "+44 …". Leaves national input untouched.
 */
const normaliseInternationalPrefix = (value: string): string =>
  value.replace(/^\++/, '+').replace(/^00(?=\d)/, '+');

function classifyPhone(raw: string, country: CountryCode): LoginIdentifier {
  const details: LoginErrorDetails = {
    selectedCountry: countryName(country),
    selectedCode: `+${getCountryCallingCode(country)}`,
  };
  const invalid = (
    reason: LoginErrorReason,
    extra?: Partial<LoginErrorDetails>,
  ): LoginIdentifier => ({
    kind: 'invalid',
    reason,
    details: { ...details, ...extra },
  });

  if (!PHONE_CHARS.test(raw)) return invalid('malformed');

  const value = normaliseInternationalPrefix(raw);

  const mismatch = (typedCallingCode: string): LoginIdentifier => {
    const typedCountry = countryForCallingCode(typedCallingCode);
    return invalid('countryMismatch', {
      typedCode: `+${typedCallingCode}`,
      typedCountry: typedCountry
        ? countryName(typedCountry)
        : `+${typedCallingCode}`,
    });
  };

  // "+33 6…" with GB picked: the user's explicit prefix disagrees with the
  // dropdown. Don't guess which one they meant.
  const typed = parsePhoneNumberFromString(value);
  if (
    value.startsWith('+') &&
    typed &&
    typed.countryCallingCode !== getCountryCallingCode(country)
  ) {
    return mismatch(typed.countryCallingCode);
  }

  // libphonenumber already absorbs "44 7911…" (country code without "+"),
  // "+44 (0)7911…" / "+44 07911…" (trunk zero after the code) and
  // "7911…" (trunk zero missing) for the selected country.
  const phone = parsePhoneNumberFromString(value, country);
  if (phone?.isValid()) return { kind: 'sms', loginHint: phone.number };

  // "0415 555 2671" with US picked: a trunk zero for a country that has none.
  if (!value.startsWith('+') && /^0\d/.test(value)) {
    const withoutZero = parsePhoneNumberFromString(value.slice(1), country);
    if (withoutZero?.isValid()) {
      return { kind: 'sms', loginHint: withoutZero.number };
    }
  }

  // "44 7911 123456" with US picked: a foreign country code typed without
  // the "+". Name the country rather than reporting "too long".
  if (!value.startsWith('+')) {
    const asInternational = parsePhoneNumberFromString(`+${value}`);
    if (
      asInternational?.isValid() &&
      asInternational.countryCallingCode !== getCountryCallingCode(country)
    ) {
      return mismatch(asInternational.countryCallingCode);
    }
  }

  switch (validatePhoneNumberLength(value, country)) {
    case 'TOO_SHORT':
      return invalid('tooShort');
    case 'TOO_LONG':
      return invalid('tooLong');
    case 'NOT_A_NUMBER':
    case 'INVALID_COUNTRY':
      return invalid('malformed');
    default:
      // Right length, but not a number that exists in this country's plan
      // (e.g. "07911 12345" for GB).
      return invalid('invalidForCountry');
  }
}

export function classifyLoginIdentifier(
  raw: string,
  country: CountryCode = DEFAULT_LOGIN_COUNTRY,
): LoginIdentifier {
  const value = raw.trim();
  if (!value) {
    return { kind: 'invalid', reason: 'empty' };
  }

  if (value.includes('@')) {
    const email = value.toLowerCase();
    return EMAIL.test(email)
      ? { kind: 'email', loginHint: email }
      : { kind: 'invalid', reason: 'malformed' };
  }

  return classifyPhone(value, country);
}
